import {
  AfterViewInit,
  DestroyRef,
  Directive,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { BaseWidgetTypeContentComponent } from '../base-widget-type-content/base-widget-type-content.component';
import { IDashboardWidgetTypeConfig } from '../models/widget-type.models';

@Directive()
export abstract class BaseChartWidget<T extends IDashboardWidgetTypeConfig>
  extends BaseWidgetTypeContentComponent<T>
  implements AfterViewInit, OnDestroy
{
  protected readonly size = signal({ width: 0, height: 0 });
  protected readonly width = computed(() => this.size().width);
  protected readonly height = computed(() => this.size().height);

  private lastWidth = 0;
  private lastHeight = 0;

  public refreshResize = input<void>();

  private el = inject(ElementRef);
  private destroyRef = inject(DestroyRef);

  private resizeObserver?: ResizeObserver;
  private rafId: number | null = null;
  private forceResizeNotify = false;
  private sizeEffectCleanup = effect(() => {
    const { width, height } = this.size();

    // Avoid noisy calls before first real measurement.
    if (width === 0 && height === 0) {
      return;
    }

    untracked(() => this.onResize());
  });

  private refreshResizeEffectCleanup = effect(() => {
    // Watch refreshResize signal; when it changes, force a resize check.
    const refreshValue = this.refreshResize();
    // Only trigger if the input is actually provided (not undefined).
    if (refreshValue !== undefined) {
      untracked(() => this.requestResizeCheck(true));
    }
  });

  public ngAfterViewInit(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.requestResizeCheck());
      this.resizeObserver.observe(this.el.nativeElement as Element);
    }

    // Initial measurement to ensure charts render correctly on first paint.
    this.requestResizeCheck();
  }

  public ngOnDestroy(): void {
    this.sizeEffectCleanup.destroy();
    this.refreshResizeEffectCleanup.destroy();

    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  /**
   * Override in concrete chart widgets if they need to reflow/redraw on resize.
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected onResize(): void {}

  private requestResizeCheck(forceNotify = false): void {
    if (forceNotify) {
      this.forceResizeNotify = true;
    }

    if (this.rafId !== null) {
      return;
    }

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.checkAndNotifyResize();
    });
  }

  private checkAndNotifyResize(): void {
    const width = (this.el.nativeElement as HTMLElement).offsetWidth ?? 0;
    const height = (this.el.nativeElement as HTMLElement).offsetHeight ?? 0;

    const changed = this.lastWidth !== width || this.lastHeight !== height;

    this.lastWidth = width;
    this.lastHeight = height;

    if (changed) {
      this.size.set({ width, height });
      return;
    }

    // If a refresh was explicitly requested, notify even if size didn't change.
    if (this.forceResizeNotify) {
      this.forceResizeNotify = false;
      untracked(() => this.onResize());
    }
  }
}
